// src/pages/admin/AdminAnalyticsPage.tsx
import React, { useState } from 'react';
import { useAllAnalytics, useExportAnalytics } from '../hooks';
import { AnalyticsCharts } from '../components';
import { useAdminStore } from '@/features/admin/stores/adminStore';
import { TimePeriod } from '@/types/admin';

export const AdminAnalyticsPage: React.FC = () => {
  const { analyticsSettings, setAnalyticsSettings } = useAdminStore();
  const [activeTab, setActiveTab] = useState<'users' | 'engagement' | 'content' | 'marketplace'>('users');
  
  const { users, engagement, content, marketplace, isLoading } = useAllAnalytics({
    period: analyticsSettings.period as TimePeriod,
  });

  const exportMutation = useExportAnalytics();

  const handleExport = () => {
    exportMutation.mutate({
      type: activeTab,
      period: analyticsSettings.period as TimePeriod,
      format: 'csv',
    });
  };

  const tabs = [
    { id: 'users', label: 'Users' },
    { id: 'engagement', label: 'Engagement' },
    { id: 'content', label: 'Content' },
    { id: 'marketplace', label: 'Marketplace' },
  ] as const;

  const getActiveData = () => {
    switch (activeTab) {
      case 'users': return users;
      case 'engagement': return engagement;
      case 'content': return content;
      case 'marketplace': return marketplace;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">Analytics</h1>
          <p className="text-warm-gray">Platform performance metrics and insights</p>
        </div>
        <div className="flex gap-3">
          <select
            value={analyticsSettings.period}
            onChange={(e) => setAnalyticsSettings({ period: e.target.value })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
            <option value="year">Last Year</option>
          </select>
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark"
          >
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-sage border-b-2 border-sage'
                  : 'text-warm-gray hover:text-navy'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="h-96 bg-gray-100 animate-pulse rounded-xl" />
          ) : (
            <AnalyticsCharts
              type={activeTab}
              data={getActiveData()}
              period={analyticsSettings.period as TimePeriod}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;



